/**
 * Login.tsx — Shield authentication page.
 *
 * Features:
 *  - Top navigation bar (Logo, Home, Plans, Contact + sign-in back button)
 *  - Email + password with show/hide toggle
 *  - Error handling: account locked, blocked, unverified, rate-limited
 *  - Role-based redirect after login
 *  - Link to forgot-password and signup
 */

import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SecurityIcon from '@mui/icons-material/Security';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { useAuth } from '../../context/AuthContext';

function formatLockedUntil(iso: string): string {
  try {
    const d = new Date(iso);
    const diff = Math.ceil((d.getTime() - Date.now()) / 60_000);
    return diff > 1 ? `${diff} minutes` : 'a moment';
  } catch {
    return 'some time';
  }
}

const NAV_LINKS = [
  { label: 'Home',    href: '/' },
  { label: 'Plans',   href: '/pricing' },
  { label: 'Contact', href: '/contact' },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [errorMsg, setErrorMsg]       = useState('');
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [pausedDialog, setPausedDialog] = useState<{ name: string } | null>(null);

  // If already logged in, redirect immediately
  React.useEffect(() => {
    if (user) redirectByRole(user.role);
  }, [user]); // eslint-disable-line

  function redirectByRole(role: string) {
    const from = (location.state as any)?.from?.pathname;
    if (from && from !== '/login') { navigate(from, { replace: true }); return; }
    if (role === 'super_admin') { navigate('/admin', { replace: true }); return; }
    navigate('/dashboard', { replace: true });
  }

  function handleNavClick(href: string) {
    setDrawerOpen(false);
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const id = href.slice(2);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(href);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg('');
    setSubmitting(true);
    try {
      const result = await login(email.trim().toLowerCase(), password);
      if (result.success) {
        if (result.account_status === 'paused') {
          setPausedDialog({ name: user?.full_name || email.split('@')[0] });
          return;
        }
        // user state will update and useEffect will redirect
      } else {
        switch (result.error_code) {
          case 'ACCOUNT_LOCKED':
            setErrorMsg(
              `Account locked due to too many failed attempts. Try again in ${formatLockedUntil(result.locked_until ?? '')}.`
            );
            break;
          case 'ACCOUNT_BLOCKED':
            setErrorMsg('Your account has been suspended. Contact support for assistance.');
            break;
          case 'EMAIL_NOT_VERIFIED':
            setErrorMsg('Please verify your email address before logging in. Check your inbox.');
            break;
          case 'RATE_LIMITED':
            setErrorMsg('Too many login attempts. Please wait a minute and try again.');
            break;
          case 'ACCOUNT_CLOSED':
            setErrorMsg('Your account has been closed. Please contact support to reactivate.');
            break;
          default:
            setErrorMsg(result.error ?? 'Invalid email or password.');
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0d1117 0%, #0a1929 100%)' }}>

      {/* ── Top nav bar ──────────────────────────────────────────────────── */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Toolbar sx={{ px: { xs: 2, sm: 4 } }}>
          {/* Logo */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: { xs: 1, sm: 0 } }}
            onClick={() => navigate('/')}
          >
            <SecurityIcon sx={{ color: '#00E5FF', fontSize: 26, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
            <Typography variant="h6" fontWeight={900} noWrap sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
          </Box>

          {/* Desktop nav links */}
          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' }, flex: 1, justifyContent: 'center' }}>
            {NAV_LINKS.map((l) => (
              <Button key={l.label} onClick={() => handleNavClick(l.href)}
                sx={{ color: 'text.secondary', fontWeight: 500, '&:hover': { color: 'text.primary' } }}>
                {l.label}
              </Button>
            ))}
          </Stack>

          {/* Desktop back button */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
              sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Back
            </Button>
          </Box>

          {/* Mobile hamburger */}
          <IconButton sx={{ display: { xs: 'flex', sm: 'none' }, color: 'text.secondary' }}
            onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260, bgcolor: 'background.paper' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((l) => (
            <ListItem key={l.label} disablePadding>
              <ListItemButton onClick={() => handleNavClick(l.href)}>
                <ListItemText primary={l.label} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); navigate(-1); }}>
              <ListItemText primary="← Back" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* ── Login card ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 6, minHeight: 'calc(100vh - 64px)' }}>
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            maxWidth: 440,
            borderRadius: 3,
            border: '1px solid rgba(0,180,216,0.15)',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => navigate('/')}
            sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
            aria-label="Go back"
          >
            <CloseIcon />
          </IconButton>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, gap: 1 }}>
            <SecurityIcon sx={{ fontSize: 36, color: '#00E5FF', filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
            <Typography variant="h4" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>
              Shield
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={4}>
            Cloud Security & AI Monitoring Platform
          </Typography>
          <Typography variant="body2" textAlign="center" mb={2}>
            <Link component={RouterLink} to="/signup" color="primary" fontWeight={600}>
              Don't have an account? Start free trial →
            </Link>
          </Typography>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email address"
              type="email"
              fullWidth
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Password"
              type={showPwd ? 'text' : 'password'}
              fullWidth
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd((p) => !p)} edge="end" size="small">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" color="primary">
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting || !email || !password}
              sx={{ fontWeight: 700, py: 1.5 }}
            >
              {submitting ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
      {/* ── Paused account dialog ────────────────────────────────────────── */}
      <Dialog open={Boolean(pausedDialog)} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle>Welcome back, {pausedDialog?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your account is currently paused. Your data is safe and will be kept for 30 days.
            Reactivate anytime to pick up exactly where you left off.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => { setPausedDialog(null); navigate('/billing'); }}
          >
            Go to Billing →
          </Button>
          <Button
            variant="text"
            onClick={() => { setPausedDialog(null); navigate('/dashboard'); }}
          >
            Just browsing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
